import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '@/api/apiClient';
import { useApp } from '@/store/AppContext';
import { GeneratedImage } from '@/types';
import { mapApiImageToGenerated, resolveImageUrl } from './utils';

const GALLERY_PAGE_SIZE = 25;

export function useGalleryData() {
  const { uuid, locale = 'en' } = useParams<{ uuid?: string; locale?: string }>();
  const navigate = useNavigate();
  const browsePath = `/${locale}/browse`;
  const {
    images,
    currentImageIndex,
    setCurrentImageIndex,
    setAllImages,
    appendImages,
    setGalleryViewMode,
    galleryViewMode,
  } = useApp();

  const [loadingExisting, setLoadingExisting] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [infoPanelVisible, setInfoPanelVisible] = useState(() => {
    const saved = localStorage.getItem('infoPanelVisible');
    return saved !== null ? saved === 'true' : true;
  });
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  const filmstripRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const shouldLoad = !hasLoadedRef.current || images.length === 0;
    if (!shouldLoad) {
      setLoadingExisting(false);
      return;
    }

    hasLoadedRef.current = true;
    let mounted = true;

    const loadExistingImages = async () => {
      try {
        const response = await apiClient.getImages({ limit: GALLERY_PAGE_SIZE, offset: 0 });
        if (!mounted) return;

        if (response.total_count !== undefined) {
          setTotalCount(response.total_count);
        }

        if (response.success && response.images) {
          const loadedImages = response.images.map(mapApiImageToGenerated);
          setAllImages(loadedImages);

          if (loadedImages.length > 0 && !uuid) {
            const firstImage = loadedImages[0];
            if (firstImage.id) {
              navigate(`${browsePath}/${firstImage.id}`, { replace: true });
            }
          }
        }
      } catch (error) {
        console.error('[Gallery] Failed to load images:', error);
      } finally {
        if (mounted) setLoadingExisting(false);
      }
    };

    loadExistingImages();
    return () => {
      mounted = false;
    };
  }, [images.length, uuid, navigate, setAllImages]);

  useEffect(() => {
    const handleToggle = () => {
      setInfoPanelVisible((prev) => {
        const newValue = !prev;
        localStorage.setItem('infoPanelVisible', newValue.toString());
        return newValue;
      });
    };
    window.addEventListener('infoPanelToggle', handleToggle);
    return () => window.removeEventListener('infoPanelToggle', handleToggle);
  }, []);

  // When another tab / PWA window generates a new image, refresh the gallery here too.
  useEffect(() => {
    const handlePwaGalleryUpdated = async () => {
      try {
        const response = await apiClient.getImages({ limit: GALLERY_PAGE_SIZE, offset: 0 });
        if (response.success && response.images) {
          const loaded = response.images.map(mapApiImageToGenerated);
          setAllImages(loaded);
          if (response.total_count !== undefined) setTotalCount(response.total_count);
        }
      } catch {
        // ignore — non-critical background refresh
      }
    };
    window.addEventListener('pwa:gallery-updated', handlePwaGalleryUpdated);
    return () => window.removeEventListener('pwa:gallery-updated', handlePwaGalleryUpdated);
  }, [setAllImages]);

  useEffect(() => {
    if (images.length > 0 && uuid) {
      const imageIndex = images.findIndex((img) => img.id === uuid);
      if (imageIndex !== -1 && imageIndex !== currentImageIndex) {
        setCurrentImageIndex(imageIndex);
      }
    }
  }, [uuid, images.length, currentImageIndex, setCurrentImageIndex]);

  // Auto-scroll filmstrip to center selected thumbnail
  useEffect(() => {
    if (galleryViewMode === 'single' && filmstripRef.current && images.length > 0) {
      const selectedIndex = uuid ? images.findIndex((img) => img.id === uuid) : currentImageIndex;
      const shouldCenter = !isDesktop || (selectedIndex > 15 && selectedIndex >= 0);

      if (shouldCenter && selectedIndex >= 0) {
        const selectedThumbnail = thumbnailRefs.current[selectedIndex];
        if (selectedThumbnail) {
          const filmstrip = filmstripRef.current;
          const thumbnailRect = selectedThumbnail.getBoundingClientRect();
          const filmstripRect = filmstrip!.getBoundingClientRect();
          const thumbnailCenter = thumbnailRect.left + thumbnailRect.width / 2;
          const filmstripCenter = filmstripRect.left + filmstripRect.width / 2;
          const scrollOffset = thumbnailCenter - filmstripCenter;
          filmstrip!.scrollBy({ left: scrollOffset, behavior: 'smooth' });
        }
      }
    }
  }, [currentImageIndex, uuid, images.length, galleryViewMode, isDesktop]);

  const handleImageSelect = useCallback(
    (index: number, switchToSingle = false) => {
      setCurrentImageIndex(index);
      const selectedImage = images[index];
      if (switchToSingle && galleryViewMode === 'grid') {
        setGalleryViewMode('single');
      }
      if (selectedImage?.id) {
        navigate(`${browsePath}/${selectedImage.id}`);
      } else {
        navigate(browsePath);
      }
    },
    [images, galleryViewMode, navigate, setCurrentImageIndex, setGalleryViewMode, browsePath]
  );

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || totalCount === null || images.length >= totalCount) return;
    setLoadingMore(true);
    try {
      const response = await apiClient.getImages({ limit: GALLERY_PAGE_SIZE, offset: images.length });
      if (response.success && response.images) {
        const newImages = response.images.map(mapApiImageToGenerated);
        appendImages(newImages);
        if (response.total_count !== undefined) {
          setTotalCount(response.total_count);
        }
        if (response.images.length === 0) {
          setTotalCount(images.length);
        }
      }
    } catch (error) {
      console.error('[Gallery] Failed to load more images:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, totalCount, images.length, appendImages]);

  const navigatePrevious = useCallback(() => {
    if (images.length === 0) return;
    const currentIndex = uuid ? images.findIndex((img) => img.id === uuid) : currentImageIndex;
    if (currentIndex === -1) return;
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    const selectedImage = images[prevIndex];
    setCurrentImageIndex(prevIndex);
    if (selectedImage?.id) {
      navigate(`${browsePath}/${selectedImage.id}`);
    } else {
      navigate(browsePath);
    }
  }, [images, uuid, currentImageIndex, navigate, setCurrentImageIndex, browsePath]);

  const navigateNext = useCallback(() => {
    if (images.length === 0) return;
    const currentIndex = uuid ? images.findIndex((img) => img.id === uuid) : currentImageIndex;
    if (currentIndex === -1) return;
    const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    const selectedImage = images[nextIndex];
    setCurrentImageIndex(nextIndex);
    if (selectedImage?.id) {
      navigate(`${browsePath}/${selectedImage.id}`);
    } else {
      navigate(browsePath);
    }
  }, [images, uuid, currentImageIndex, navigate, setCurrentImageIndex, browsePath]);

  return {
    uuid,
    images,
    currentImageIndex,
    loadingExisting,
    totalCount,
    loadingMore,
    infoPanelVisible,
    setInfoPanelVisible,
    isDesktop,
    filmstripRef,
    thumbnailRefs,
    handleImageSelect,
    handleLoadMore,
    navigatePrevious,
    navigateNext,
    setTotalCount,
  };
}
